import FalconCardHeader from 'components/common/FalconCardHeader';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTablePagination from 'components/common/advance-table/AdvanceTablePagination';
import AdvanceTableWrapper from 'components/common/advance-table/AdvanceTableWrapper';
import { useMemo, useState } from 'react';
import { Card, Dropdown } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import CardDropdown from 'components/common/CardDropdown';
import { AuthDel } from 'helpers/api/AuthCRUD';
import { API } from 'helpers/api/endpoints';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { Address } from 'types';
import AddAddressModal from './AddAddressModal';
import EditAddressModal from './EditAddressModal';

type MainComProps = {
  addresses: Address[] | undefined;
  refetch: () => void;
  customerGuid: string;
};
type ColumsProps = {
  removeAddress: (id: any) => void;
  refetch: () => void;
  customerGuid: string;
  t: ReturnType<typeof useTranslation>['t'];
};

const columns = ({ removeAddress, customerGuid, refetch, t }: ColumsProps) => {
  return [
    {
      accessorKey: 'strasse',
      header: t('street', { ns: 'labels' }),
      meta: {
        headerProps: { className: 'ps-2', style: { height: '46px' } },
        cellProps: {
          className: 'py-2 white-space-nowrap pe-3 pe-xxl-4 ps-2',
        },
      },
      cell: (rowData: any) => {
        const { strasse } = rowData.row.original;
        return <h6 className="mb-0 text-900">{strasse}</h6>;
      },
    },
    {
      accessorKey: 'plz',
      header: t('postal_code', { ns: 'labels' }),
      meta: {
        headerProps: { style: { minWidth: '14.625rem' } },
        cellProps: {
          className: 'py-2 pe-4',
        },
      },
      cell: (rowData: any) => {
        const { plz } = rowData.row.original;
        return <h6 className="mb-0 text-900">{plz}</h6>;
      },
    },
    {
      accessorKey: 'stadt',
      header: t('city', { ns: 'labels' }),
      meta: {
        cellProps: {
          className: 'fs-0 pe-4',
        },
      },
      cell: (rowData: any) => {
        const { stadt } = rowData.row.original;
        return <h6 className="mb-0 text-900">{stadt}</h6>;
      },
    },
    {
      accessorKey: 'land',
      header: t('country', { ns: 'labels' }),
      meta: {
        cellProps: {
          className: 'pe-4',
        },
      },
      cell: (rowData: any) => {
        const { land } = rowData.row.original;
        return <h6 className="mb-0 text-900">{land}</h6>;
      },
    },
    {
      accessorKey: 'bundesland',
      header: t('state', { ns: 'labels' }),
      cell: (rowData: any) => {
        const { bundesland } = rowData.row.original;
        return <h6 className="mb-0 text-900">{bundesland}</h6>;
      },
    },
    {
      id: 'Action',
      header: t('Action', { ns: 'benutzerliste' }),
      meta: {
        headerProps: {
          className: 'text-end',
        },
        cellProps: {
          className: 'text-end',
        },
      },
      enableSorting: false,
      cell: (rowData: any) => {
        return (
          <AddressActions
            address={rowData.row.original}
            customerGuid={customerGuid}
            refetch={refetch}
            removeAddress={removeAddress}
            t={t}
          />
        );
      },
    },
  ];
};
type AddressActionsProps = {
  address: Address;
  customerGuid: string;
  refetch: () => void;
  removeAddress: (guid: any) => void;
  t: ReturnType<typeof useTranslation>['t'];
};

const AddressActions = ({
  address,
  customerGuid,
  refetch,
  removeAddress,
  t,
}: AddressActionsProps) => {
  const [showEdit, setShowEdit] = useState(false);

  return (
    <>
      <CardDropdown btnRevealClass="btn-reveal-sm" drop="start">
        <div className="py-2">
          <Dropdown.Item onClick={() => setShowEdit(true)}>
            {t('edit', { ns: 'benutzerliste' })}
          </Dropdown.Item>
          <Dropdown.Divider />
          <Dropdown.Item
            className="text-danger"
            onClick={() => removeAddress((address as any).guid)}
          >
            {t('Delete', { ns: 'benutzerliste' })}
          </Dropdown.Item>
        </div>
      </CardDropdown>

      <EditAddressModal
        user={address as any}
        customerGuid={customerGuid}
        refetch={refetch}
        show={showEdit}
        onHide={() => setShowEdit(false)}
      />
    </>
  );
};

const AddressTable = ({ addresses, refetch, customerGuid }: MainComProps) => {
  const { token } = useSelector((state: RootState) => state);
  const { t } = useTranslation();

  const removeAddress = async (guid: any) => {
    if (!token) return;
    await AuthDel(API.addresses.delete(customerGuid, guid), {}, token);
    refetch();
  };
  const tableColumns = useMemo(
    () => columns({ removeAddress, customerGuid, refetch, t }),
    [customerGuid, refetch, t]
  );
  return (
    <AdvanceTableWrapper
      //@ts-ignore
      rowCount={addresses.length}
      columns={tableColumns}
      data={addresses}
      sortable
      // selection
      pagination={true}
      perPage={8}
      selectionColumnWidth={10}
    >
      <Card className="mb-xl-3">
        <FalconCardHeader
          title={t('addresses', { ns: 'labels' })}
          titleTag="h6"
          endEl={
            <AddAddressModal refetch={refetch} customerGuid={customerGuid} />
          }
        />
        <Card.Body>
          <AdvanceTable
            //@ts-ignore

            table
            // headerClassName="d-none"
            rowClassName=" align-middle white-space-nowrap hover-actions-trigger btn-reveal-trigger hover-bg-100 "
            tableProps={{
              className: 'fs--1 mb-0 overflow-hidden',
            }}
          />
        </Card.Body>
        <Card.Footer>
          <AdvanceTablePagination
            //@ts-ignore
            table
          />
        </Card.Footer>
      </Card>
    </AdvanceTableWrapper>
  );
};

export default AddressTable;
